import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  agentById, countryByCode, liveQuotesByCountry, liveCountryCodes,
  liveAgentIds, liveParcelSpec,
} from "@/data";
import { money, eta as fmtEta, pct } from "@/format";

type RankMode = "cheapest" | "fastest" | "value";
const RANK_MODES: { id: RankMode; label: string }[] = [
  { id: "cheapest", label: "Cheapest" },
  { id: "fastest", label: "Fastest" },
  { id: "value", label: "Best value" },
];

const midEta = (a: number | null, b: number | null) =>
  a != null && b != null ? (a + b) / 2 : a ?? b ?? null;

export default function CalculatorPage() {
  const [params, setParams] = useSearchParams();
  const country = params.get("c") ?? (liveCountryCodes.includes("US") ? "US" : liveCountryCodes[0]);
  const mode = (params.get("m") ?? "cheapest") as RankMode;
  const [q, setQ] = useState("");
  const [fx, setFx] = useState(0);

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    let list = (liveQuotesByCountry.get(country) ?? []).slice();
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter(
        (r) => r.lineName.toLowerCase().includes(needle) ||
          (agentById.get(r.agentId)?.name ?? r.agentId).toLowerCase().includes(needle)
      );
    }
    const prices = list.map((r) => r.priceCny!);
    const etas = list.map((r) => midEta(r.etaMinDays, r.etaMaxDays)).filter((x): x is number => x != null);
    const pMin = Math.min(...prices), pMax = Math.max(...prices);
    const eMin = Math.min(...etas), eMax = Math.max(...etas);
    const norm = (v: number, lo: number, hi: number) => (hi > lo ? 1 - (v - lo) / (hi - lo) : 1);
    const scored = list.map((r) => {
      const e = midEta(r.etaMinDays, r.etaMaxDays);
      const value = 0.62 * norm(r.priceCny!, pMin, pMax) + 0.38 * (e != null ? norm(e, eMin, eMax) : 0);
      return { ...r, value };
    });
    scored.sort((a, b) => {
      if (mode === "cheapest") return a.priceCny! - b.priceCny!;
      if (mode === "fastest") return (midEta(a.etaMinDays, a.etaMaxDays) ?? 1e9) - (midEta(b.etaMinDays, b.etaMaxDays) ?? 1e9);
      return b.value - a.value;
    });
    return scored;
  }, [country, mode, q]);

  const usd = (cny: number, fallback: number | null) =>
    fallback != null ? fallback : fx > 0 ? Math.round(cny * fx * 100) / 100 : null;

  const cheapest = rows.length ? Math.min(...rows.map((r) => r.priceCny!)) : 0;

  return (
    <div>
      <div className="row spread wrap" style={{ gap: 8 }}>
        <div>
          <h1 style={{ marginBottom: 2 }}>Live shipping comparison</h1>
          <p className="muted" style={{ margin: 0 }}>
            Every price is captured live from each agent’s <strong>own</strong> shipping calculator —
            100% real, nothing modeled.
          </p>
        </div>
        <span className="pill-live" title="Fixed reference parcel used for every capture">
          📦 {(liveParcelSpec?.weightG ?? 5000) / 1000} kg · {liveParcelSpec?.lengthCm}×{liveParcelSpec?.widthCm}×{liveParcelSpec?.heightCm} cm
        </span>
      </div>

      <div className="card card-pad mt calc-controls">
        <label className="field">
          <span>Destination</span>
          <select value={country} onChange={(e) => set("c", e.target.value)}>
            {liveCountryCodes.map((c) => (
              <option key={c} value={c}>{countryByCode.get(c)?.name ?? c}</option>
            ))}
          </select>
        </label>
        <label className="field" style={{ gridColumn: "span 2" }}>
          <span>Filter lines / agents</span>
          <input className="input" placeholder="e.g. DHL, EMS, superbuy…" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <label className="field">
          <span>CNY→USD (optional)</span>
          <input className="input" type="number" step="0.01" placeholder="e.g. 0.14" value={fx || ""} onChange={(e) => setFx(Number(e.target.value))} />
        </label>
      </div>

      <div className="row wrap mt" style={{ gap: 6 }}>
        {RANK_MODES.map((m) => (
          <button key={m.id} className={`chip ${mode === m.id ? "on" : ""}`} onClick={() => set("m", m.id)} style={{ cursor: "pointer" }}>
            {m.label}
          </button>
        ))}
        <span className="grow" />
        <span className="tiny faint" style={{ alignSelf: "center" }}>
          {rows.length} live lines · {liveAgentIds.length} agents · {liveQuotesByCountry.size ? liveCountryCodes.length : 0} destinations
        </span>
      </div>

      <div className="table-wrap mt sticky-1">
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: 30 }}>#</th>
              <th>Agent</th>
              <th>Shipping line</th>
              <th className="num">Price</th>
              <th className="num">ETA</th>
              <th className="num">Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const u = usd(r.priceCny!, r.priceUsd);
              const best = r.priceCny === cheapest && mode === "cheapest";
              return (
                <tr key={r.id}>
                  <td className="faint">{i + 1}</td>
                  <td>
                    <Link to={`/agent/${r.agentId}`} className="row" style={{ gap: 8 }}>
                      <span className="avatar" style={{ width: 26, height: 26, borderRadius: 7, fontSize: "0.7rem" }}>
                        {(agentById.get(r.agentId)?.logoText ?? r.agentId.slice(0, 2)).toUpperCase()}
                      </span>
                      <strong style={{ color: "var(--text)" }}>{agentById.get(r.agentId)?.name ?? r.agentId}</strong>
                    </Link>
                  </td>
                  <td>{r.lineName}{r.brandsAllowed === false && <span className="tiny faint"> · no brands</span>}</td>
                  <td className="num">
                    <strong style={best ? { color: "var(--good)" } : undefined}>{money(r.priceCny!, "CNY")}</strong>
                    {u != null && <span className="tiny faint"> ≈{money(u, "USD")}</span>}
                  </td>
                  <td className="num">{fmtEta(r.etaMinDays, r.etaMaxDays)}</td>
                  <td className="num">{pct(r.value)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && (
        <div className="empty">No live captures for {countryByCode.get(country)?.name ?? country} yet.</div>
      )}
      <p className="tiny faint mt">
        Prices are the agent’s own quoted total (incl. their fees) for the reference parcel above, captured
        as a guest with no account. “Value” = 62% price + 38% speed, normalized within this destination.
        Refresh weekly with <code>scraper/fetch_all.py</code>.
      </p>
    </div>
  );
}
